import * as React from 'react';
import { Sizes } from '../../enums/sizes';
import { cn } from '../../utils/cn';
import { inputVariants } from '../../variants/input-variants';

export type InputSize = Sizes.SM | Sizes.MD | Sizes.LG;

export interface InputProps extends Omit<
  React.ComponentProps<'input'>,
  'size'
> {
  size?: InputSize;
  leading?: React.ReactNode;
  trailing?: React.ReactNode;
  inputClassName?: string;
}

function Input({
  className,
  type,
  size = Sizes.SM,
  leading,
  trailing,
  disabled,
  readOnly,
  inputClassName,
  ref,
  ...props
}: InputProps) {
  const inputRef = React.useRef<HTMLInputElement | null>(null);

  const setRefs = (node: HTMLInputElement | null) => {
    inputRef.current = node;
    if (typeof ref === 'function') {
      ref(node);
      return;
    }
    if (ref) {
      ref.current = node;
    }
  };

  const handleShellMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
    if (disabled) {
      return;
    }

    const target = event.target as HTMLElement;
    // Bail when the click lands on an interactive child so its native handler fires instead of stealing focus.
    if (target.closest('button, a, input, textarea, select, [role="button"]')) {
      return;
    }

    if (document.activeElement === inputRef.current) {
      return;
    }

    event.preventDefault();
    inputRef.current?.focus();
  };

  return (
    <div
      data-slot='input'
      data-size={size}
      className={cn(
        inputVariants({ size }),
        disabled && 'cursor-not-allowed',
        className
      )}
      onMouseDown={handleShellMouseDown}
    >
      {leading !== undefined && leading !== null && (
        <span data-slot='input-leading' className='flex shrink-0 items-center'>
          {leading}
        </span>
      )}
      <input
        type={type}
        {...props}
        ref={setRefs}
        disabled={disabled}
        readOnly={readOnly}
        tabIndex={disabled ? -1 : props.tabIndex}
        className={cn(
          'file:text-sm file:font-medium file:text-primary min-w-0 flex-1 border-0 bg-transparent p-0 text-primary shadow-none outline-none placeholder:text-placeholder selection:bg-brand-solid selection:text-primary_on-brand file:inline-flex file:h-7 file:border-0 file:bg-transparent disabled:cursor-not-allowed disabled:text-placeholder disabled:[-webkit-text-fill-color:var(--color-text-placeholder)]',
          inputClassName
        )}
      />
      {trailing !== undefined && trailing !== null && (
        <span data-slot='input-trailing' className='flex shrink-0 items-center'>
          {trailing}
        </span>
      )}
    </div>
  );
}

export { Input };
